import React, { useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Send, ExpandMore } from '@mui/icons-material';

// Base URL for API (for debugging)
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4000' 
  : '';

const GrokRequestForm = ({ characters, kinks = [], items = [] }) => {
  const [playerId, setPlayerId] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [characterId, setCharacterId] = useState('');
  const [prompt, setPrompt] = useState('');
  const [selectedKinks, setSelectedKinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [afterJoiSession, setAfterJoiSession] = useState(false);
  const [completedTasks, setCompletedTasks] = useState('');
  const [refusedTasks, setRefusedTasks] = useState('');
  const [afterControlSession, setAfterControlSession] = useState(false);
  const [sessionHistory, setSessionHistory] = useState('');
  const [relationshipType, setRelationshipType] = useState('neutral');
  const [selectedGift, setSelectedGift] = useState('');
  const [showGiftBlock, setShowGiftBlock] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!playerId.trim() || !playerName.trim() || !characterId || !prompt.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');
    setResponse(null);

    const selectedCharacter = characters.find(c => c._id === characterId);
    const formattedPrompt = `${playerName}: ${prompt.trim()}`;
    
    // Формируем полный промпт с историей
    const historyText = chatHistory.map(msg => msg.content).join('\n');
    const fullPrompt = historyText ? `${historyText}\n${formattedPrompt}` : formattedPrompt;
    
    // Добавляем сообщение игрока в историю
    const playerMessage = {
      type: 'player',
      content: formattedPrompt,
      timestamp: new Date().toISOString()
    };
    
    setChatHistory(prev => [...prev, playerMessage]);

    try {
      const response = await fetch(`${API_BASE_URL}/api/grok/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playerId: playerId.trim(),
          playerName: playerName.trim(),
          characterId,
          prompt: fullPrompt,
          characterData: selectedCharacter ? {
            name: selectedCharacter.name,
            instruction: selectedCharacter.instruction,
            relationshipType: relationshipType,
            neutral: selectedCharacter.neutral || '',
            trusting: selectedCharacter.trusting || '',
            obedient: selectedCharacter.obedient || '',
            devoted: selectedCharacter.devoted || ''
          } : null,
          kinksData: selectedKinkObjects.map(kink => ({
            name: kink.name,
            instruction: kink.instruction
          })),
          afterJoiSession,
          completedTasks: completedTasks.trim(),
          refusedTasks: refusedTasks.trim(),
          selectedGift: selectedGift ? {
            name: items.find(i => i._id === selectedGift)?.name || '',
            instruction: items.find(i => i._id === selectedGift)?.instruction || ''
          } : null,
          afterControlSession,
          sessionHistory: sessionHistory.trim()
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Request error');
      }

      // Добавляем ответ AI в историю
      const extras = [];
      if (typeof data.relationship_delta === 'number' && data.relationship_delta !== 0) {
        extras.push(`relationship_delta: ${data.relationship_delta}`);
      }
      if (Array.isArray(data.new_facts) && data.new_facts.length > 0) {
        extras.push(`new_facts: ${JSON.stringify(data.new_facts)}`);
      }
      if (data.factsSaved === false) {
        extras.push('factsSaved: false');
      }
      if (typeof data.suggested_set_id === 'string' && data.suggested_set_id.trim() !== '') {
        extras.push(`suggested_set_id: ${data.suggested_set_id.trim()}`);
      }
      const suffix = extras.length ? `\n[${extras.join(' | ')}]` : '';
      const aiMessage = {
        type: 'ai',
        content: `${selectedCharacter?.name || 'AI'}: ${data.message}${suffix}`,
        timestamp: new Date().toISOString()
      };
      
      setChatHistory(prev => [...prev, aiMessage]);
      setResponse(data);
      setPrompt(''); // Очищаем поле ввода после отправки
      setAfterJoiSession(false); // Сбрасываем галочку JOI session
      setCompletedTasks(''); // Очищаем поле выполненных задач
      setRefusedTasks(''); // Очищаем поле отказанных задач
      setAfterControlSession(false); // Сбрасываем галочку Control session
      setSessionHistory(''); // Очищаем поле истории сессии
      setSelectedGift(''); // Сбрасываем выбор подарка
      setShowGiftBlock(false); // Скрываем блок подарка
      // НЕ сбрасываем relationshipType, чтобы сохранить выбор
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKinkChange = (event) => {
    const value = event.target.value;
    setSelectedKinks(typeof value === 'string' ? value.split(',') : value);
  };

  const selectedCharacter = characters.find(c => c._id === characterId);
  const selectedKinkObjects = kinks.filter(kink => selectedKinks.includes(kink._id));

  return (
    <Box>
      <Box component="form" onSubmit={handleSubmit} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Player ID"
            value={playerId}
            onChange={(e) => setPlayerId(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Player Name"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            sx={{ mb: 2 }}
          />
          
          <FormControl fullWidth margin="normal" required disabled={loading} sx={{ mb: 2 }}>
            <InputLabel id="character-select-label">Character</InputLabel>
            <Select
              labelId="character-select-label"
              id="character-select"
              value={characterId}
              label="Character"
              onChange={(e) => setCharacterId(e.target.value)}
            >
              {characters.map((character) => (
                <MenuItem key={character._id} value={character._id}>
                  {character.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedCharacter && (
            <FormControl fullWidth margin="normal" disabled={loading} sx={{ mb: 2 }}>
              <InputLabel id="relationship-select-label">Relationship Type</InputLabel>
              <Select
                labelId="relationship-select-label"
                id="relationship-select"
                value={relationshipType}
                label="Relationship Type"
                onChange={(e) => setRelationshipType(e.target.value)}
              >
                <MenuItem value="neutral">Neutral</MenuItem>
                <MenuItem value="trusting">Trusting</MenuItem>
                <MenuItem value="obedient">Obedient</MenuItem>
                <MenuItem value="devoted">Devoted</MenuItem>
              </Select>
            </FormControl>
          )}

          <FormControl fullWidth margin="normal" disabled={loading} sx={{ mb: 2 }}>
            <InputLabel id="kinks-select-label">Kinks (Optional)</InputLabel>
            <Select
              labelId="kinks-select-label"
              id="kinks-select"
              multiple
              value={selectedKinks}
              onChange={handleKinkChange}
              input={<OutlinedInput label="Kinks (Optional)" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const kink = kinks.find(k => k._id === value);
                    return (
                      <Chip 
                        key={value} 
                        label={kink ? kink.name : value} 
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
            >
              {kinks.map((kink) => (
                <MenuItem key={kink._id} value={kink._id}>
                  {kink.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>



          {/* Chat History */}
          {chatHistory.length > 0 && (
            <Box sx={{ mt: 2, mb: 2, maxHeight: '300px', overflow: 'auto' }}>
              <Typography variant="h6" gutterBottom>
                Chat History
              </Typography>
              <Box sx={{ 
                border: 1, 
                borderColor: 'divider',
                borderRadius: 1, 
                p: 2, 
                bgcolor: 'action.hover',
                maxHeight: '250px',
                overflow: 'auto'
              }}>
                {chatHistory.map((message, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      mb: 1, 
                      p: 1, 
                      backgroundColor: message.type === 'player' ? 'rgba(56, 189, 248, 0.12)' : 'rgba(167, 139, 250, 0.14)',
                      borderRadius: 1,
                      borderLeft: '4px solid',
                      borderLeftColor: message.type === 'player' ? 'primary.main' : 'secondary.main'
                    }}
                  >
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {message.content}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* After JOI Session Block */}
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={afterJoiSession}
                  onChange={(e) => setAfterJoiSession(e.target.checked)}
                  disabled={loading}
                />
              }
              label="After JOI Session"
            />
            
            {afterJoiSession && (
              <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                <TextField
                  fullWidth
                  label="What player completed"
                  value={completedTasks}
                  onChange={(e) => setCompletedTasks(e.target.value)}
                  margin="normal"
                  disabled={loading}
                  multiline
                  rows={2}
                  placeholder="Tasks that player successfully completed..."
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  label="What player refused"
                  value={refusedTasks}
                  onChange={(e) => setRefusedTasks(e.target.value)}
                  margin="normal"
                  disabled={loading}
                  multiline
                  rows={2}
                  placeholder="Tasks that player refused to do..."
                  sx={{ mb: 2 }}
                />
              </Box>
            )}
          </Box>

          {/* After Control Session Block */}
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={afterControlSession}
                  onChange={(e) => setAfterControlSession(e.target.checked)}
                  disabled={loading}
                />
              }
              label="After Control Session"
            />
            
            {afterControlSession && (
              <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                <TextField
                  fullWidth
                  label="Session History"
                  value={sessionHistory}
                  onChange={(e) => setSessionHistory(e.target.value)}
                  margin="normal"
                  disabled={loading}
                  multiline
                  rows={4}
                  placeholder="Describe what happened during the control session..."
                  sx={{ mb: 2 }}
                />
              </Box>
            )}
          </Box>

          {/* Gift Selection Block */}
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showGiftBlock}
                  onChange={(e) => {
                    setShowGiftBlock(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedGift('');
                    }
                  }}
                  disabled={loading}
                />
              }
              label="Give Gift"
            />
            
            {showGiftBlock && (
              <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
                <FormControl fullWidth margin="normal" disabled={loading} sx={{ mb: 2 }}>
                  <InputLabel id="gift-select-label">Select Gift</InputLabel>
                  <Select
                    labelId="gift-select-label"
                    id="gift-select"
                    value={selectedGift}
                    label="Select Gift"
                    onChange={(e) => setSelectedGift(e.target.value)}
                  >
                    {items.filter((item) => item.type === 'Gift').map((item) => (
                      <MenuItem key={item._id} value={item._id}>
                        <Box>
                          <Typography variant="body1">{item.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Cost: {item.cost}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {selectedGift && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.selected', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Gift Details:
                    </Typography>
                    {(() => {
                      const item = items.find(i => i._id === selectedGift);
                      return item ? (
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Name:</strong> {item.name}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Cost:</strong> {item.cost}
                          </Typography>
                          {item.description && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Description:</strong> {item.description}
                            </Typography>
                          )}
                          {item.instruction && (
                            <Typography variant="body2" sx={{ 
                              bgcolor: 'rgba(255,255,255,0.06)',
                              border: 1,
                              borderColor: 'divider',
                              p: 1, 
                              borderRadius: 1,
                              fontSize: '0.875rem',
                              color: 'text.secondary'
                            }}>
                              <strong>Instruction:</strong> {item.instruction}
                            </Typography>
                          )}
                        </Box>
                      ) : null;
                    })()}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <TextField
            fullWidth
            label="Message to AI"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            multiline
            rows={3}
            placeholder="Type your message here..."
            sx={{ mb: 2 }}
          />

          <Button
            type="submit"
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <Send />}
            disabled={loading || !playerId.trim() || !playerName.trim() || !characterId || !prompt.trim()}
            sx={{ mt: 2 }}
            fullWidth
          >
            {loading ? 'Sending...' : 'Send Request'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {response && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              AI Response:
            </Typography>
            <Box
              sx={{
                bgcolor: 'rgba(167, 139, 250, 0.12)',
                p: 1,
                borderRadius: 1,
                borderLeft: 4,
                borderLeftColor: 'secondary.main',
                borderLeftStyle: 'solid'
              }}
            >
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {response.message}
              </Typography>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip size="small" label={`relationship_delta: ${response.relationship_delta ?? 0}`} />
              <Chip
                size="small"
                color={response.suggested_set_id ? 'primary' : 'default'}
                label={
                  response.suggested_set_id
                    ? `suggested_set_id: ${response.suggested_set_id}`
                    : 'suggested_set_id: (none)'
                }
              />
              <Chip
                size="small"
                color={response.factsSaved === false ? 'warning' : 'default'}
                label={response.factsSaved === false ? 'facts not saved (server)' : 'facts saved'}
              />
            </Box>
            {Array.isArray(response.new_facts) && response.new_facts.length > 0 && (
              <Typography variant="caption" component="div" sx={{ mt: 1, display: 'block', fontFamily: 'monospace' }}>
                new_facts: {JSON.stringify(response.new_facts)}
              </Typography>
            )}
          </Alert>
        )}

        {/* AI Request Details */}
        {response && (
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="ai-request-content"
              id="ai-request-header"
            >
              <Typography variant="h6">AI Request Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Selected Character:
                </Typography>
                <Box sx={{ 
                  bgcolor: 'action.hover', 
                  p: 1.5, 
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  mb: 2
                }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify({
                      name: selectedCharacter?.name || 'Unknown',
                      instruction: selectedCharacter?.instruction || 'No instruction available'
                    }, null, 2)}
                  </Typography>
                </Box>
              </Box>

              {selectedKinkObjects.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Selected Kinks:
                  </Typography>
                  <Box sx={{ 
                    bgcolor: 'action.hover', 
                    p: 1.5, 
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider'
                  }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(selectedKinkObjects.map(kink => ({
                        name: kink.name,
                        instruction: kink.instruction
                      })), null, 2)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        )}
    </Box>
  );
};

export default GrokRequestForm; 